import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Input, InputNumber, Select, Switch, DatePicker, Upload, message, Alert, Card } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faTrash, faListUl, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
// react-quill-new = Quill 2.0.3. KHÔNG dùng react-quill@2 (Quill 1.3.7): bug
// desync index ở emoji/surrogate pair → in đậm & link bị xuống hàng, chèn space
// giữa từ (đã fix y hệt bên editor blog 27/06 — xem PostEditor.js).
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import rewardProgramApi from "../../../api/rewardProgramApi";
import DynamicConfigField from "./dynamic-config-field";
import EstimatePanel from "./reward-estimate-panel";
import { rewardImageUrl } from "./reward-image-url";
import { normalizeQuillHtml } from "../../../utils/quill-html-normalize";
import { buildWhitelistTermsHtml } from "./reward-whitelist-terms-html";

const EMPTY = {
    name: "", slug: "", type: "budget_multiplier", enabled: false, status: "draft",
    dateRange: [null, null], thumbnail: null, bannerImage: null, shortDesc: "",
    termsHtml: "", totalBudget: null, perUserCap: null, requireOptIn: true,
    isHot: false, priority: 0, config: {},
    displayUsedBudget: null, displayParticipants: null,
};

const moneyProps = {
    formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
    parser: (v) => v.replace(/,/g, ""),
};

/** Form tạo/sửa đợt thưởng — chia section card; phần riêng theo type render ĐỘNG từ configFields BE. */
function RewardProgramForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const [types, setTypes] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [estimating, setEstimating] = useState(false);
    const [estimate, setEstimate] = useState(null);
    const [uploading, setUploading] = useState(null); // field đang upload

    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    const setConfig = (key, value) => setForm((prev) => ({ ...prev, config: { ...prev.config, [key]: value } }));
    const typeDef = types.find((t) => t.key === form.type);
    const quillRef = useRef(null);

    // Toolbar Quill có nút ảnh: handler mặc định nhét base64 vào HTML → vỡ cột
    // TEXT (64KB) ngay 1 ảnh. Thay bằng upload qua API reward rồi chèn URL tuyệt đối
    // (ảnh serve từ api.longhousee.com — đường dẫn tương đối sẽ 404 trên web/app).
    const quillModules = useMemo(() => ({
        toolbar: {
            container: [["bold", "italic"], [{ list: "ordered" }, { list: "bullet" }], ["link", "image"], ["clean"]],
            handlers: {
                image() {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.onchange = async () => {
                        const file = input.files && input.files[0];
                        if (!file) return;
                        try {
                            const { data } = await rewardProgramApi.uploadImage(file);
                            const quill = quillRef.current?.getEditor();
                            if (!quill) return;
                            const index = quill.getSelection(true)?.index ?? quill.getLength();
                            quill.insertEmbed(index, "image", rewardImageUrl(data.filename));
                            quill.setSelection(index + 1);
                        } catch (err) {
                            message.error(err?.response?.data?.message || "Upload ảnh thất bại");
                        }
                    };
                    input.click();
                },
            },
        },
        clipboard: { matchVisual: false },
    }), []);

    /**
     * Nạp đợt từ server vào form. Gọi lại được sau khi Lưu để form hiện đúng bản
     * BE đã chuẩn hoá (whitelist đã dedupe, tên/ảnh vừa tra) — không phải đoán
     * xem state trên máy có khớp DB hay không.
     */
    const loadProgram = useCallback(async () => {
        const res = await rewardProgramApi.list();
        const p = (res.data.programs || []).find((x) => String(x.id) === String(id));
        if (!p) { message.error("Không tìm thấy chương trình"); navigate("/reward-program"); return; }
        setForm({
            name: p.name, slug: p.slug, type: p.type, enabled: !!p.enabled, status: p.status,
            dateRange: [p.startTime ? dayjs.unix(p.startTime) : null, p.endTime ? dayjs.unix(p.endTime) : null],
            thumbnail: p.thumbnail, bannerImage: p.bannerImage, shortDesc: p.shortDesc || "",
            termsHtml: p.termsHtml || "", totalBudget: p.totalBudget, perUserCap: p.perUserCap,
            requireOptIn: !!p.requireOptIn, isHot: !!p.isHot, priority: p.priority || 0,
            config: (typeof p.config === "string" ? JSON.parse(p.config) : p.config) || {},
            displayUsedBudget: p.displayUsedBudget, displayParticipants: p.displayParticipants,
        });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        (async () => {
            try {
                const { data } = await rewardProgramApi.getTypes();
                setTypes(data.types || []);
                if (isEdit) await loadProgram();
            } catch { message.error("Không tải được dữ liệu"); }
        })();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // antd Upload trả UploadFile WRAPPER — phải lấy originFileObj, append thẳng
    // wrapper vào FormData là backend không nhận được file (bug upload 28/07).
    const uploadImage = (field) => async ({ file }) => {
        const raw = file?.originFileObj || file;
        if (!(raw instanceof Blob)) return;
        setUploading(field);
        try {
            const { data } = await rewardProgramApi.uploadImage(raw);
            set(field, data.filename);
            message.success("Đã upload ảnh");
        } catch (err) {
            message.error(err?.response?.data?.message || "Upload thất bại");
        } finally {
            setUploading(null);
        }
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) return message.error("Nhập tên chương trình");
        if (!form.slug.trim()) return message.error("Nhập slug (vd: x2-hoan-tien-8-8)");
        const payload = {
            ...form,
            // Quill 2 để MỌI list trong <ol> + li[data-list] → web/app sẽ đánh số
            // cho cả list dấu chấm. Chuẩn hoá về <ul>/<ol> thật trước khi lưu.
            termsHtml: normalizeQuillHtml(form.termsHtml),
            startTime: form.dateRange[0] ? form.dateRange[0].unix() : null,
            endTime: form.dateRange[1] ? form.dateRange[1].unix() : null,
        };
        delete payload.dateRange;
        setLoading(true);
        try {
            if (isEdit) {
                await rewardProgramApi.update(id, payload);
                message.success("Đã cập nhật");
                // Ở LẠI trang sửa: chỉnh whitelist thường phải lưu → xem lại → chỉnh tiếp,
                // đá về danh sách là mỗi vòng mất 2 cú bấm. Nạp lại từ server để form
                // hiện đúng bản BE đã chuẩn hoá (dedupe, tên/ảnh vừa tra).
                await loadProgram();
            } else {
                await rewardProgramApi.create(payload);
                message.success("Đã tạo đợt mới");
                navigate("/reward-program");
            }
        } catch (err) {
            // BE trả 400 khi trùng đợt nhân hệ số (kể cả Chiến dịch X2 cũ) — hiện nguyên văn.
            message.error(err?.response?.data?.message || "Có lỗi xảy ra", 6);
        } finally { setLoading(false); }
    };

    const refEstimate = form.totalBudget ? Math.round(form.totalBudget * 0.1) : 0;

    /**
     * Chèn danh sách shop/sản phẩm áp dụng vào Điều khoản.
     * Dán vào VỊ TRÍ CON TRỎ (dangerouslyPasteHTML), không ghi đè nội dung đang có —
     * admin thường đã soạn thể lệ trước rồi mới thêm danh sách.
     */
    const handleInsertWhitelist = () => {
        const html = buildWhitelistTermsHtml(form.config);
        if (!html) return message.warning("Đợt này chưa giới hạn shop/sản phẩm nào");
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const index = quill.getSelection(true)?.index ?? quill.getLength();
        quill.clipboard.dangerouslyPasteHTML(index, html, "user");
        message.success("Đã chèn danh sách — sửa lại chữ nếu cần");
    };

    const whitelistCount =
        (form.config?.shopWhitelist?.length || 0) + (form.config?.productWhitelist?.length || 0);

    /**
     * Ước lượng đợt ĐANG ĐIỀN: BE chạy cấu hình này qua đúng engine chốt lô với đơn
     * 30 ngày gần nhất (chỉ đọc). Cho biết trước bao nhiêu người nhận, chi bao nhiêu,
     * phải bù bao nhiêu tiền túi và ngân sách chạy được mấy ngày.
     */
    const handleEstimate = async () => {
        const payload = {
            ...form,
            termsHtml: normalizeQuillHtml(form.termsHtml),
            startTime: form.dateRange[0] ? form.dateRange[0].unix() : null,
            endTime: form.dateRange[1] ? form.dateRange[1].unix() : null,
        };
        delete payload.dateRange;
        setEstimating(true);
        setEstimate(null);
        try {
            const res = await rewardProgramApi.estimate(payload, 30);
            setEstimate(res.data.estimate);
        } catch (err) {
            // BE validate config ngay ở đây → cấu hình sai biết liền, không đợi lúc Lưu.
            message.error(err?.response?.data?.message || "Không ước lượng được", 6);
        } finally { setEstimating(false); }
    };

    // Ô upload ảnh có preview + nút xoá. Là HÀM trả JSX, KHÔNG phải component
    // định nghĩa trong render — làm vậy mỗi lần setState React sẽ coi là type mới
    // và remount, làm mất trạng thái upload đang chạy.
    const renderImageField = (field, label, hint, previewClass) => (
        <div key={field}>
            <label className="block mb-1 font-medium">{label}</label>
            <p className="text-xs text-content-3 mb-2">{hint}</p>
            {rewardImageUrl(form[field]) ? (
                <div className="relative inline-block group">
                    <img src={rewardImageUrl(form[field])} alt="" className={previewClass} />
                    <Button
                        size="small" danger className="absolute top-1 right-1"
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => set(field, null)}
                    />
                </div>
            ) : (
                <Upload showUploadList={false} beforeUpload={() => false} onChange={uploadImage(field)} accept="image/*">
                    <Button icon={<FontAwesomeIcon icon={faUpload} />} loading={uploading === field}>
                        Chọn ảnh
                    </Button>
                </Upload>
            )}
        </div>
    );

    return (
        // max-w-5xl (không phải 3xl): bảng whitelist có 6 cột (sàn/id/ảnh/tên/link/nút),
        // ở 3xl cột Tên chỉ còn hơn trăm px, tên sản phẩm sàn thì dài cả dòng.
        // Ô nhập hẹp KHÔNG bị kéo giãn theo vì đã tự chặn bằng max-w-2xl bên dưới.
        <div className="p-6 max-w-5xl mx-auto pb-24">
            <h1 className="text-2xl font-bold mb-6">{isEdit ? `Sửa đợt thưởng #${id}` : "Tạo đợt thưởng mới"}</h1>
            <div className="space-y-4">
                <Card size="small" title="📋 Thông tin cơ bản">
                    <div className="space-y-4">
                        <div>
                            <label className="block mb-1 font-medium">Tên chương trình *</label>
                            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="VD: X2 Hoa Hồng App 8/8" />
                        </div>
                        <div>
                            <label className="block mb-1 font-medium">Slug (deep link) *</label>
                            <Input value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} placeholder="x2-hoan-tien-8-8" />
                            <p className="text-xs text-content-3 mt-1">
                                Link:{" "}
                                {form.slug ? (
                                    // Mở TAB MỚI: form đang điền dở, điều hướng cùng tab là mất hết.
                                    <a
                                        href={`https://longhousee.com/chuong-trinh-thuong/${form.slug}`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline"
                                    >
                                        <code>longhousee.com/chuong-trinh-thuong/{form.slug}</code>
                                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ml-1" />
                                    </a>
                                ) : (
                                    <code>longhousee.com/chuong-trinh-thuong/...</code>
                                )}
                                {isEdit && <span className="text-amber-600"> — đổi slug làm gãy link đã chia sẻ trước đó</span>}
                            </p>
                        </div>
                        <div>
                            <label className="block mb-1 font-medium">Mô tả ngắn (hiện trên card)</label>
                            <Input value={form.shortDesc} onChange={(e) => set("shortDesc", e.target.value)} maxLength={255} showCount />
                        </div>
                        <div>
                            <label className="block mb-1 font-medium">Loại chương trình *</label>
                            <Select value={form.type} onChange={(v) => { set("type", v); set("config", {}); }} className="w-full" disabled={isEdit}
                                options={types.map((t) => ({ value: t.key, label: t.label }))} />
                        </div>
                        {typeDef && (
                            <div className="border border-blue-100 bg-blue-50 rounded-lg p-4 space-y-3">
                                <div className="font-medium text-blue-900">Cấu hình: {typeDef.label}</div>
                                {typeDef.configFields.map((f) =>
                                    // Field rộng (ma trận theo sàn, danh sách link) xuống dòng riêng,
                                    // không kẹp cạnh label
                                    f.type === "camp_matrix" || f.type === "link_list" || f.type === "tier_table" ? (
                                        <div key={f.key} className="space-y-1">
                                            <label className="font-medium">{f.label}</label>
                                            <DynamicConfigField field={f} value={form.config[f.key]} onChange={setConfig} />
                                        </div>
                                    ) : (
                                        // hint của field hẹp phải render Ở ĐÂY: DynamicConfigField chỉ
                                        // vẽ ô nhập, còn camp_matrix/link_list tự lo hint bên trong.
                                        <div key={f.key} className="flex items-start gap-3">
                                            <label className="w-56 shrink-0 pt-1">{f.label}</label>
                                            {/* flex-1 + min-w-0: ô nhập tự giãn hết chiều ngang còn lại,
                                                nhãn option dài mới đọc được (trước đóng cứng 220px) */}
                                            <div className="flex-1 min-w-0 max-w-2xl space-y-1">
                                                <DynamicConfigField field={f} value={form.config[f.key]} onChange={setConfig} />
                                                {f.hint && <p className="text-xs text-content-3 max-w-lg">{f.hint}</p>}
                                            </div>
                                        </div>
                                    )
                                )}
                                {/* Cảnh báo chi phí khi nhân cả phần Xtra — chi tăng mạnh so với chỉ nhân cơ bản */}
                                {(form.config.scope === "all" ||
                                    Object.values(form.config.campOverrides || {}).some((r) => r?.scope === "all")) && (
                                    <Alert
                                        type="warning" showIcon
                                        message="Phạm vi 'Toàn bộ hoa hồng' nhân cả phần Xtra/seller — chi phí mỗi đơn cao hơn đáng kể so với 'Chỉ cơ bản'. Ngân sách sẽ cạn nhanh hơn."
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </Card>

                <Card size="small" title="🕐 Thời gian & Ngân sách">
                    <div className="space-y-4">
                        <div>
                            <label className="block mb-1 font-medium">Cửa sổ thời gian (xét theo giờ MUA của đơn)</label>
                            <DatePicker.RangePicker showTime={{ format: "HH:mm:ss" }} format="DD/MM/YYYY HH:mm:ss" value={form.dateRange}
                                onChange={(vals) => set("dateRange", vals ? [vals[0], vals[1]] : [null, null])} className="w-full" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block mb-1 font-medium">Ngân sách cả đợt (đ)</label>
                                <InputNumber value={form.totalBudget} onChange={(v) => set("totalBudget", v)} className="w-full" {...moneyProps} placeholder="Trống = không giới hạn" />
                            </div>
                            <div>
                                <label className="block mb-1 font-medium">Trần thưởng mỗi user (đ)</label>
                                <InputNumber value={form.perUserCap} onChange={(v) => set("perUserCap", v)} className="w-full" {...moneyProps} placeholder="Trống = không trần" />
                            </div>
                        </div>
                        {form.totalBudget ? (
                            <Alert type="warning" showIcon message={`Chi thực tế ước tính: ${Number(form.totalBudget).toLocaleString("vi-VN")}đ (ngân sách) + tối đa ~${refEstimate.toLocaleString("vi-VN")}đ hoa hồng giới thiệu (5–10%, NGOÀI ngân sách).`} />
                        ) : null}

                        {/* Thử cấu hình trên dữ liệu thật TRƯỚC khi bật đợt — tránh cảnh
                            bật xong mới biết quỹ cháy trong 1 tuần hoặc chẳng ai đủ điều kiện. */}
                        <div className="flex items-center gap-3">
                            <Button onClick={handleEstimate} loading={estimating}>Ước lượng trên đơn 30 ngày gần nhất</Button>
                            <span className="text-xs text-content-3">Chạy thử cấu hình hiện tại qua engine chia thưởng — chỉ đọc, không tạo đợt.</span>
                        </div>
                        {estimate && <EstimatePanel estimate={estimate} />}
                    </div>
                </Card>

                <Card size="small" title="🖼 Hình ảnh">
                    <div className="grid grid-cols-2 gap-6">
                        {renderImageField("thumbnail", "Ảnh tròn (card)", "Vuông ≥ 256px — hiện trên card list + màn chi tiết", "w-24 h-24 rounded-full object-cover border")}
                        {renderImageField("bannerImage", "Banner ngang (thể lệ)", "Tỉ lệ ~2.2:1 — hiện trong phần điều khoản", "max-h-28 rounded-lg object-cover border")}
                    </div>
                </Card>

                <Card
                    size="small" title="📜 Điều khoản & điều kiện"
                    // Nút chèn danh sách: app CŨ chưa có block "áp dụng cho" nên đây là
                    // đường duy nhất để user máy cũ đọc được đợt giới hạn shop/sản phẩm.
                    extra={whitelistCount > 0 && (
                        <Button size="small" icon={<FontAwesomeIcon icon={faListUl} />} onClick={handleInsertWhitelist}>
                            Chèn danh sách shop/sản phẩm ({whitelistCount})
                        </Button>
                    )}
                >
                    <ReactQuill ref={quillRef} theme="snow" value={form.termsHtml} onChange={(v) => set("termsHtml", v)}
                        modules={quillModules} />
                    {whitelistCount > 0 && (
                        <p className="text-xs text-content-3 mt-2">
                            Chèn tại vị trí con trỏ. Danh sách chèn ra là VĂN BẢN — sửa/xoá ở đây không đổi luật xét thưởng
                            (luật lấy từ ô "Chỉ thưởng shop/sản phẩm" ở trên). Đổi whitelist thì chèn lại.
                        </p>
                    )}
                </Card>

                <Card size="small" title="🎭 Số liệu hiển thị (marketing)">
                    <p className="text-xs text-content-3 mb-3">
                        Số điền tay ĐÈ LÊN số thật khi hiển thị cho user (app + web). Để trống = dùng số thật.
                        Không ảnh hưởng phân bổ tiền — job chia thưởng luôn dùng số thật.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-1 font-medium">Ngân sách đã chi (hiển thị)</label>
                            <InputNumber value={form.displayUsedBudget} onChange={(v) => set("displayUsedBudget", v)} className="w-full" {...moneyProps} placeholder="Trống = số thật" />
                        </div>
                        <div>
                            <label className="block mb-1 font-medium">Số người tham gia (hiển thị)</label>
                            <InputNumber value={form.displayParticipants} onChange={(v) => set("displayParticipants", v)} className="w-full" {...moneyProps} placeholder="Trống = số thật" />
                        </div>
                    </div>
                </Card>

                <Card size="small" title="⚙️ Hiển thị & kích hoạt">
                    <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                        {[["enabled", "Bật đợt"], ["requireOptIn", "Bắt buộc bấm Tham gia"], ["isHot", "Badge HOT"]].map(([k, label]) => (
                            <div key={k} className="flex items-center gap-2">
                                <label className="font-medium">{label}</label>
                                <Switch checked={!!form[k]} onChange={(v) => set(k, v)} />
                            </div>
                        ))}
                        <div className="flex items-center gap-2">
                            <label className="font-medium">Ưu tiên</label>
                            <InputNumber value={form.priority} onChange={(v) => set("priority", v || 0)} style={{ width: 80 }} />
                        </div>
                    </div>
                    <Alert className="mt-3" type="info" showIcon message="Bật đợt TRƯỚC 00:00 ngày bắt đầu — tạo sau khi đơn đã crawl sẽ phải chạy bù tay. Đơn mua ngày D chia thưởng lúc 09:30 ngày D+2." />
                </Card>
            </div>

            {/* Footer sticky */}
            <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-line px-6 py-3 flex justify-end gap-2 z-10">
                <Button onClick={() => navigate("/reward-program")}>Huỷ</Button>
                <Button type="primary" onClick={handleSubmit} loading={loading}>{isEdit ? "Cập nhật" : "Tạo mới"}</Button>
            </div>
        </div>
    );
}

export default RewardProgramForm;
